import React, { useState } from 'react';
import { FaSearch, FaPlus, FaTimes, FaSpinner } from 'react-icons/fa';

interface WordSuggestion {
  base_form: string;
  translation: string;
  grammar: string;
  word_type: string;
  example: string;
  example_translation: string;
}

interface AddWordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWordAdded: () => void;
  currentLanguage: string; // e.g., "de-DE"
  nativeLanguage: string; // e.g., "en-US"
}

const baseUrl: string = process.env.REACT_APP_BACKEND_URL || "";

const AddWordModal: React.FC<AddWordModalProps> = ({
  isOpen,
  onClose,
  onWordAdded,
  currentLanguage,
  nativeLanguage,
}) => {
  const [word, setWord] = useState('');
  const [suggestions, setSuggestions] = useState<WordSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const handleSearch = async () => {
    if (!word.trim()) {
      setError('Please enter a word');
      return;
    }

    setLoading(true);
    setError(null);
    setSuggestions([]);

    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${baseUrl}/dictionary/suggest_word_meanings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          word: word.trim(),
          word_language: currentLanguage,
          translation_language: nativeLanguage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get word suggestions');
      }

      const data = await response.json();
      setSuggestions(data.suggestions || []);

      if (!data.suggestions || data.suggestions.length === 0) {
        setError('No translations found for this word');
      }
    } catch (err: any) {
      console.error('Error fetching word suggestions:', err);
      setError(err.message || 'Failed to get word suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWord = async (suggestion: WordSuggestion) => {
    setAdding(true);
    setError(null);

    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch(`${baseUrl}/dictionary/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          word_base_form: suggestion.base_form,
          word_translation: suggestion.translation,
          grammar: suggestion.grammar,
          example_sentence: suggestion.example,
          example_sentence_translation: suggestion.example_translation,
          word_original_language: currentLanguage,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.detail && errorData.detail.includes('already have this word')) {
          throw new Error('This word is already in your dictionary');
        }
        throw new Error('Failed to add word to dictionary');
      }

      // Success!
      setWord('');
      setSuggestions([]);
      onWordAdded();
      onClose();
    } catch (err: any) {
      console.error('Error adding word:', err);
      setError(err.message || 'Failed to add word to dictionary');
    } finally {
      setAdding(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleSearch();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-base-300">
          <h3 className="text-xl font-bold">Add Word to Dictionary</h3>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-circle"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Search Input */}
          <div className="mb-4">
            <label className="label">
              <span className="label-text">Enter a word</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type any word..."
                className="input input-bordered flex-1"
                disabled={loading}
              />
              <button
                onClick={handleSearch}
                disabled={loading || !word.trim()}
                className="btn btn-primary"
              >
                {loading ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaSearch />
                )}
                Search
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-error mb-4">
              <span>{error}</span>
            </div>
          )}

          {/* Suggestions List */}
          {suggestions.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-base-content/70">
                Choose a translation:
              </h4>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="card bg-base-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="card-body p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-bold text-lg">
                            {suggestion.base_form}
                          </h5>
                          <span className="badge badge-primary">
                            {suggestion.grammar}
                          </span>
                        </div>
                        <p className="text-base-content/80 mb-3">
                          → {suggestion.translation}
                        </p>
                        <div className="space-y-1 text-sm">
                          <p className="italic text-base-content/70">
                            "{suggestion.example}"
                          </p>
                          <p className="text-base-content/60">
                            "{suggestion.example_translation}"
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAddWord(suggestion)}
                        disabled={adding}
                        className="btn btn-primary btn-sm flex items-center gap-2"
                      >
                        {adding ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <FaPlus />
                        )}
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && suggestions.length === 0 && !word && (
            <div className="text-center py-8 text-base-content/60">
              <FaSearch className="mx-auto text-4xl mb-3 opacity-30" />
              <p>Enter a word to see translation suggestions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddWordModal;
